'use client';

import { useCallback } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import NiceModal from '@ebay/nice-modal-react';
import { MegaphoneIcon } from '@heroicons/react/24/solid';
import { Button } from '@components/common';
import { InterviewQuestionnaireModal } from './InterviewQuestionnaireModal';

export interface InterviewResolverProps {
  /** Visual presentation of the trigger. */
  variant: 'banner' | 'button' | 'icon';
  /** Resolved at open time — each use case supplies its own question set. */
  getQuestions: () => Promise<string[]>;
  locale: Locale;
  className?: string;
  /** Optional CTA / aria label (banner title / button text). */
  label?: string;
  /** Optional banner description; falls back to the default resume-interview copy. */
  description?: string;
  /** Invoked when the questionnaire finishes / closes (e.g. refresh the answered gate). */
  onFinished?: () => void;
}

/**
 * Reusable entry point for the resume interview. Renders one of three triggers (banner card, plain
 * button or icon-only button); on tap it fetches the questions and opens the question-by-question
 * questionnaire modal (text / audio / video answers). Questions are resolved lazily at open time.
 */
export const InterviewResolver = ({
  variant, getQuestions, locale, className, label, description, onFinished,
}: InterviewResolverProps) => {
  const { t } = useTranslation('common');
  const openQuestionnaire = useCallback(() => {
    void NiceModal.show(InterviewQuestionnaireModal, {
      getQuestions,
      locale,
      firstMessage: t('audio-interview.bestie.message'),
      onFinished,
    });
  }, [getQuestions, locale, onFinished, t]);

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={openQuestionnaire}
        aria-label={label ?? t('ai.interview.start')}
        className={clsx(
          'flex size-10 items-center justify-center rounded-full bg-surface text-secondary transition-colors hover:bg-surface-100',
          className,
        )}
      >
        <MegaphoneIcon className="size-5" />
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <Button
        color="secondary"
        rounding="full"
        onClick={openQuestionnaire}
        className={clsx('inline-flex items-center gap-2', className)}
        aria-label={label}
      >
        <MegaphoneIcon className="size-5" />
        {label ?? t('ai.interview.banner.title')}
      </Button>
    );
  }

  // variant === 'banner'
  return (
    <div
      className={clsx(
        'flex flex-col gap-4 rounded-3xl border border-ink-border p-6 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex items-start gap-4">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
          <MegaphoneIcon className="size-6" />
        </span>
        <div>
          <h3 className="text-lg font-bold">{label ?? t('ai.interview.banner.title')}</h3>
          <p className="mt-1 text-sm text-ink-medium">
            {description ?? t('ai.interview.banner.description')}
          </p>
        </div>
      </div>
      <Button
        color="secondary"
        rounding="full"
        onClick={openQuestionnaire}
        className="shrink-0"
      >
        {t('ai.interview.start')}
      </Button>
    </div>
  );
};
